import { useEffect, useMemo, useState, Fragment, useContext } from 'react';
import {
  Box,
  Text,
  Badge,
  Notification,
  Flex,
  useMantineTheme,
  useCombobox,
  Stack,
  Group,
  Combobox,
  InputBase,
  Input,
  Button,
} from '@mantine/core';
import DaysOfWeekDisplayLine from 'components/ui/DaysOfWeekDisplayLine/DaysOfWeekDisplayLine';
import classes from './SessionSelect.module.scss';
import { ActivityDetails as ActivityDetailsType, Product, ProductsByDate } from 'types';
import { ActivityBlockSubtypeEnum, ActivityTypeEnum } from 'enums';
import { useBasket } from 'context/BasketContext';
import { UtmParamsData } from 'components/ActivityDetails/ActivityDetails';
import { CalendarBlank, CaretDown, CaretUp, Clock, Info, Ticket } from '@phosphor-icons/react';
import TimesAndAvailabilityEmbed from '../ActivityDetails/TimesAndAvailability/InstantBookTimesAndAvailabilityEmbed';
import SubsTimesAndAvailabilityEmbed from '../ActivityDetails/TimesAndAvailability/SubsTimesAndAvailabilityEmbed';
import { redirectToAdpFromIframe } from 'utils/redirectToIframe';
import { EmbedContext, EmbedContextType } from 'context/EmbedContext';
interface ISessionSelectProps {
  activityDetails: ActivityDetailsType;
  selectedSession: Product | null;
  setSelectedSession(val: Product | null): void;
  setUserSelectedRadio?(val: boolean): void;
  isStandalone?: boolean;
  blockBookingTabActive: boolean;
  setBlockBookingTabActive: React.Dispatch<React.SetStateAction<boolean>>;
  utmParams?: UtmParamsData;
}

interface IClassesOptionsProps {
  name: string;
  dateRange: string;
  timeRanges: string[];
}

const ClassesOptions: React.FC<IClassesOptionsProps> = ({ name, dateRange, timeRanges }) => {
  const theme = useMantineTheme();

  return (
    <Stack gap={12} className={classes.classesOptions}>
      <Text c={theme.colors.blue[8]} size="md" fw={600}>
        {name}
      </Text>
      <Flex align={'center'} gap={8}>
        <CalendarBlank size={20} />
        <Text c={theme.colors.blue[8]} size="md" fw={600}>
          {dateRange}
        </Text>
      </Flex>
      <Group gap={12}>
        {timeRanges.map((time, index) => {
          return (
            <Flex gap={8} key={index}>
              <Clock size={20} />
              <Text c={theme.colors.gray[6]} fw={600} size={'sm'}>
                {time}
              </Text>
            </Flex>
          );
        })}
      </Group>
    </Stack>
  );
};

const SessionSelectEmbed: React.FC<ISessionSelectProps> = ({
  activityDetails,
  selectedSession,
  setSelectedSession,
  setUserSelectedRadio,
  blockBookingTabActive,
  setBlockBookingTabActive,
}) => {
  const theme = useMantineTheme();
  const { activity, classes: classesObj } = activityDetails;
  const { iframeLocation } = useContext(EmbedContext) as EmbedContextType;
  const { selectedClass, setSelectedClass, setBasketItems } = useBasket();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const isSubscription = activity.activityType === ActivityTypeEnum.SUBSCRIPTION;

  const isSingleClass = classesObj.length === 1;

  const [showSessionSelect, setShowSessionSelect] = useState<boolean>(
    isSubscription || isSingleClass,
  );
  const [showSelectionError, setShowSelectionError] = useState<boolean>(false);

  useEffect(() => {
    if (classesObj.length === 1) {
      setSelectedClass(classesObj[0]);
    }
  }, [classesObj, setSelectedClass]);

  const someDatesSoldOut = useMemo(() => {
    if (isSubscription) {
      return false;
    }
    const { blocks, sessions } = activityDetails.classes[0];

    const soldOutBlock = activity.allowBlockBookings
      ? blocks.some((booking) =>
          booking.allBlockDates?.some((date) => date.spotsLeft === 0 && !date.isInPast),
        )
      : false;

    const soldOutSingle = activity.allowIndividualBookings
      ? sessions.some((session) => session.products?.some((product) => product.spotsLeft === 0))
      : false;

    return soldOutBlock || soldOutSingle;
  }, [
    activityDetails,
    activity.allowBlockBookings,
    activity.allowIndividualBookings,
    isSubscription,
  ]);

  const handlePebbleRedirect = async () => {
    // return if no session
    if (!selectedSession) {
      return;
    }

    redirectToAdpFromIframe(
      selectedSession,
      activityDetails?.activity.slug || '',
      iframeLocation,
      [],
    );
  };

  const getTimeRanges = (blocks: Product[], sessions: ProductsByDate[]) => {
    const timeRanges = new Set();

    blocks.forEach((block) => {
      const timeRange = `${block.startTime} - ${block.endTime}`;
      timeRanges.add(timeRange);
    });

    sessions.forEach((session) => {
      session.products.forEach((product) => {
        const timeRange = `${product.startTime} - ${product.endTime}`;
        timeRanges.add(timeRange);
      });
    });

    return Array.from(timeRanges);
  };

  const options = classesObj.map(({ name, id, blocks, sessions, dateRange }) => {
    const timeRanges = getTimeRanges(blocks, sessions) as string[];

    return (
      <Fragment key={id}>
        <Combobox.Option value={id} key={id} className={classes.classSelect}>
          <ClassesOptions name={name} dateRange={dateRange} timeRanges={timeRanges} />
        </Combobox.Option>
        <div className={classes.dotted}></div>
      </Fragment>
    );
  });

  return (
    <>
      <Flex direction={'column'} mx={0}>
        {activity.activityType !== ActivityTypeEnum.SUBSCRIPTION && !isSingleClass && (
          <Box miw={300}>
            <Combobox
              store={combobox}
              onOptionSubmit={(val) => {
                setSelectedClass(classesObj.filter((singleClass) => singleClass.id === val)[0]);
                setShowSelectionError(false);
                setShowSessionSelect(true);
                combobox.closeDropdown();

                //   Reset basket context state on unmount
                setBasketItems([]);
              }}
            >
              <Combobox.Target>
                <InputBase
                  label={
                    <Flex align={'center'} gap={8} mb={12} fs="lg">
                      <Text fw={700} c={theme.colors.blue[8]}>
                        Make a booking
                      </Text>
                    </Flex>
                  }
                  component="button"
                  type="button"
                  pointer
                  rightSection={
                    combobox.dropdownOpened ? (
                      <CaretUp size={16} color={theme.colors.blue[8]} />
                    ) : (
                      <CaretDown size={16} color={theme.colors.blue[8]} />
                    )
                  }
                  rightSectionPointerEvents="none"
                  onClick={() => combobox.toggleDropdown()}
                  classNames={{
                    input: classes.bookingSelectInput,
                    section: classes.bookingSelectSection,
                  }}
                >
                  {selectedClass ? (
                    <Text c={theme.colors.blue[8]} fw={600} size="md">
                      {selectedClass.name}
                    </Text>
                  ) : (
                    <Input.Placeholder className={classes.classSelectPlaceholder}>
                      Please select a class
                    </Input.Placeholder>
                  )}
                </InputBase>
              </Combobox.Target>

              <Combobox.Dropdown className={classes.dropdown}>
                <Combobox.Options>{options}</Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
            {showSelectionError && (
              <Text c={theme.colors.red[6]} size="sm">
                Please select an option
              </Text>
            )}
          </Box>
        )}
        {showSessionSelect && (selectedClass || isSubscription) && (
          <>
            <Box>
              {(selectedClass?.dateRange ||
                activity.activityType === ActivityTypeEnum.SUBSCRIPTION) && (
                <Box className={classes.detailsItemDateRange}>
                  <CalendarBlank size={20} color={theme.colors.blue[8]} />
                  <Text component="p" c={theme.colors.blue[8]} fw={600} size="md">
                    {activity.activityType === ActivityTypeEnum.SUBSCRIPTION
                      ? 'Weekly sessions'
                      : selectedClass?.dateRange}
                  </Text>
                </Box>
              )}
              {activity.activityType !== ActivityTypeEnum.ONE_OFF && (
                <Box className={classes.daysOfWeekDisplayLine}>
                  <DaysOfWeekDisplayLine
                    weekdays={selectedClass?.weekdays || activityDetails.weekdays || []}
                    selectedLimeBg
                    fullWidth
                    enlarged
                  />
                </Box>
              )}
              {someDatesSoldOut && (
                <Badge className={classes.someDatesSoldOut}>
                  {!activityDetails.anySpotsLeft ? 'All dates sold out' : 'Some dates sold out'}
                </Badge>
              )}
              {activity?.blockSubtype === ActivityBlockSubtypeEnum.ALL_DAYS && (
                <Notification
                  withCloseButton={false}
                  radius={0}
                  classNames={{
                    icon: classes.notificationIcon,
                    root: classes.notificationRoot,
                    title: classes.notificationTitle,
                  }}
                  icon={<Info size={26} color={theme.colors.blue[8]} />}
                  title={
                    selectedClass?.sessions.length === 0
                      ? 'Tickets will be booked for all dates listed.'
                      : 'Tickets will be booked for all dates listed \n or you can book a single session in the Single tab.'
                          .split('\n')
                          .map((paragraph, key) => <div key={key}>{paragraph}</div>)
                  }
                />
              )}
            </Box>
            <Flex direction={'column'} align={'center'} mb={12}>
              <Box
                component="section"
                aria-label="times and availability"
                id="times-and-availability"
                className={(classes.anchorTag, classes.card)}
              >
                {selectedClass && !isSubscription ? (
                  <TimesAndAvailabilityEmbed
                    activityDetails={activityDetails}
                    selectedClass={selectedClass}
                    blockBookingTabActive={blockBookingTabActive}
                    setBlockBookingTabActive={setBlockBookingTabActive}
                    selectedSession={selectedSession}
                    setSelectedSession={setSelectedSession}
                    setUserSelectedRadio={setUserSelectedRadio}
                    handlePebbleRedirect={handlePebbleRedirect}
                  />
                ) : (
                  <SubsTimesAndAvailabilityEmbed
                    activityDetails={activityDetails}
                    selectedSession={selectedSession}
                    setSelectedSession={setSelectedSession}
                    setUserSelectedRadio={setUserSelectedRadio}
                    handlePebbleRedirect={handlePebbleRedirect}
                  />
                )}
              </Box>
            </Flex>
          </>
        )}
      </Flex>
      {!showSessionSelect && (
        <Button
          mt={12}
          mb={18}
          className={classes.chooseTicketsBtn}
          leftSection={<Ticket size={16} />}
          onClick={() => {
            if (!selectedClass) return setShowSelectionError(true);
            setShowSessionSelect(true);
          }}
          fullWidth={true}
        >
          Choose tickets
        </Button>
      )}
    </>
  );
};

export default SessionSelectEmbed;
